export const EMBED_CHANNEL = 'ssm.embed';

export const EMBED_VERSION = 1;

export const MESSAGE_TYPES = {
    ACTION: 'action',
    CLOSE: 'close',
    INIT: 'init',
    NOTIFY: 'notify',
    READY: 'ready',
    REQUEST_CONTEXT_REFRESH: 'requestContextRefresh',
    RESIZE: 'resize',
    STATE: 'state',
};

const CHILD_TO_PARENT_TYPES = new Set([
    MESSAGE_TYPES.CLOSE,
    MESSAGE_TYPES.NOTIFY,
    MESSAGE_TYPES.READY,
    MESSAGE_TYPES.REQUEST_CONTEXT_REFRESH,
    MESSAGE_TYPES.RESIZE,
    MESSAGE_TYPES.STATE,
]);

const PARENT_TO_CHILD_TYPES = new Set([MESSAGE_TYPES.ACTION, MESSAGE_TYPES.INIT]);

export const DEFAULT_IFRAME_TITLE = 'External embed';

export const DEFAULT_IFRAME_MIN_HEIGHT_PX = 320;
// `allow-same-origin` keeps postMessage origin validation usable. Do not embed untrusted
// same-origin content with this default, because scripts could relax their own sandbox.
export const DEFAULT_IFRAME_SANDBOX = 'allow-scripts allow-forms allow-same-origin';
export const RESIZE_HEIGHT_MAX_PX = 10000;
export const RESIZE_THROTTLE_MS = 100;
export const DEFAULT_HANDSHAKE_TIMEOUT_MS = 15000;
export const DEFAULT_CHILD_CANCEL_TIMEOUT_MS = 10000;

export const CLOSE_REASONS = {
    CANCEL: 'cancel',
    FAILED: 'failed',
    SAVED: 'saved',
    SUBMITTED: 'submitted',
};

export const HOST_CHROME_CANCEL_ACTION = 'cancel';
export const HOST_CHROME_CANCEL_SOURCE = 'hostChrome';

export const SSM_EMBED_GLOBAL = '__SSM_EMBED__';
export const SSM_EMBED_ANDROID_INTERFACE = 'ssmEmbed';
export const SSM_EMBED_IOS_HANDLER = 'ssmEmbed';

export const UNAUTHORIZED_REFRESH_LIMIT_ERROR = 'Unauthorized context refresh limit reached';

export const isKnownCloseReason = (reason) => {
    return Object.values(CLOSE_REASONS).includes(reason);
};

export const normalizeClosePayload = (payload) => {
    if (payload == null || typeof payload !== 'object' || Array.isArray(payload)) {
        return {};
    }

    let cloned;
    try {
        cloned = JSON.parse(JSON.stringify(payload));
    } catch {
        return {};
    }

    if (typeof cloned.reason !== 'string') {
        delete cloned.reason;
        return cloned;
    }

    const normalizedReason = cloned.reason.trim().toLowerCase();
    if (isKnownCloseReason(normalizedReason)) {
        cloned.reason = normalizedReason;
    } else {
        delete cloned.reason;
    }

    return cloned;
};

export const createEmbedEnvelope = (type, payload = {}) => ({
    channel: EMBED_CHANNEL,
    payload,
    type,
    version: EMBED_VERSION,
});

export const isValidEmbedEnvelope = (data) => {
    return (
        data !== null &&
        typeof data === 'object' &&
        data.channel === EMBED_CHANNEL &&
        data.version === EMBED_VERSION &&
        typeof data.type === 'string'
    );
};

export const isChildToParentMessage = (data) => {
    if (!isValidEmbedEnvelope(data)) return false;
    return CHILD_TO_PARENT_TYPES.has(data.type);
};

export const isParentToChildMessage = (data) => {
    if (!isValidEmbedEnvelope(data)) return false;
    return PARENT_TO_CHILD_TYPES.has(data.type);
};

export const parseEmbedMessage = (message) => {
    let candidate = message;

    if (
        message !== null &&
        typeof message === 'object' &&
        'data' in message &&
        !isValidEmbedEnvelope(message)
    ) {
        candidate = message.data;
    }

    if (typeof candidate === 'string') {
        try {
            return JSON.parse(candidate);
        } catch {
            return null;
        }
    }

    return candidate;
};

export const getAllowedOriginFromSrc = (iframeSrc) => {
    try {
        return new URL(iframeSrc).origin;
    } catch {
        return null;
    }
};

export const getOriginFromUrl = (value) => {
    try {
        return value ? new URL(value).origin : null;
    } catch {
        return null;
    }
};

export const getParentOriginFromWindow = (win = window) => {
    try {
        const fromQuery = new URLSearchParams(win.location.search).get('parentOrigin');
        const queryOrigin = getOriginFromUrl(fromQuery);
        if (queryOrigin) return queryOrigin;
    } catch {
        // Ignore invalid window.location access.
    }

    return getOriginFromUrl(win.document?.referrer) ?? getAncestorOriginFromWindow(win);
};

const getAncestorOriginFromWindow = (win) => {
    try {
        return getOriginFromUrl(win.location?.ancestorOrigins?.[0]);
    } catch {
        return null;
    }
};

export const normalizeResizeHeight = (value) => {
    const height = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(height) || height <= 0) return null;
    return Math.min(Math.floor(height), RESIZE_HEIGHT_MAX_PX);
};

export const throttleResize = (callback, waitMs = RESIZE_THROTTLE_MS) => {
    let lastRun = 0;
    let trailingTimer = null;
    let trailingHeight = null;

    return (height) => {
        const now = Date.now();
        const elapsed = now - lastRun;

        const invoke = (nextHeight) => {
            lastRun = Date.now();
            callback(nextHeight);
        };

        if (elapsed >= waitMs) {
            if (trailingTimer) {
                clearTimeout(trailingTimer);
                trailingTimer = null;
            }
            invoke(height);
            return;
        }

        trailingHeight = height;

        if (!trailingTimer) {
            trailingTimer = setTimeout(() => {
                trailingTimer = null;
                invoke(trailingHeight);
            }, waitMs - elapsed);
        }
    };
};
